import { FC } from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredString } from "~/utils/validations";
import { MilestoneSelectInput } from "./components";

type Values = {
  upcomingMilestone: string;
};

const validationSchema = Yup.object({
  upcomingMilestone: requiredString,
});

export const NextMilestonePage: FC = () => {
  const { onSubmit } = useSubmitOnboardingQuestion();
  const { upcomingMilestone, journeyLooksLike } = useOnboarding();
  // This step is necessary because this used to be multi-select and is stored as an Array on the backend.
  // We convert the string value submitted on this page to an Array before persisting in the Onboarding::Updater
  const selectedJourney = journeyLooksLike?.[0] || "";

  const initialValues: Values = {
    upcomingMilestone: upcomingMilestone || "",
  };

  const getOptionsForJourney = (journey: string) => {
    switch (journey) {
      case "fertility_check":
        return [
          {
            label:
              "My doctor and I are still figuring out my protocol — doing tests, waiting on next steps, waiting for period to start",
            value: "fertility_check_workup",
          },
        ];
      case "tic_oi":
        return [
          {
            label: "I'm taking medication and actively monitoring — tracking my cycle with my clinic",
            value: "tic_oi_monitoring_medicated",
          },
          {
            label: "I am not using any medication and actively monitoring — tracking my cycle with my clinic",
            value: "tic_oi_monitoring_unmedicated",
          },
          {
            label: "I'm in my two week wait period",
            value: "tic_oi_two_week_wait",
          },
        ];
      case "iui":
        return [
          {
            label: "My doctor and I are prepping and waiting on next steps",
            value: "iui_iui_prep",
          },
          {
            label: "I've started my IUI cycle — taking medications and going in for monitoring",
            value: "iui_cycle_started_medicated",
          },
          {
            label: "I've started my IUI cycle (no medication) and going in for monitoring",
            value: "iui_cycle_started_unmedicated",
          },
          {
            label: "I've had my IUI procedure and I'm waiting on pregnancy results",
            value: "iui_waiting_pregnancy_results",
          },
        ];
      case "ivf":
        return [
          {
            label: "I have a protocol and I'm preparing for my cycle — but haven't started medications yet",
            value: "ivf_ivf_prep",
          },
          { label: "I've started medications for this cycle", value: "ivf_started_stims" },
          {
            label: "I've had my retrieval and I'm waiting to hear about my embryos or transfer plan",
            value: "ivf_embryo_grading_pgt",
          },
          {
            label: "I've had my transfer and I'm waiting on results",
            value: "ivf_waiting_pregnancy_results",
          },
        ];
      case "surrogate_donor":
        return [
          {
            label: "I have a protocol and I'm preparing for my cycle — but haven't started medications yet",
            value: "surrogate_donor_ivf_prep",
          },
          { label: "I've started medications for this cycle", value: "surrogate_donor_started_stims" },
          {
            label: "I've had my retrieval and I'm waiting to hear about my embryos or transfer plan",
            value: "surrogate_donor_embryo_grading_pgt",
          },
          {
            label: "I've had my transfer and I'm waiting on results",
            value: "surrogate_donor_waiting_pregnancy_results",
          },
        ];
      case "donor_egg":
        return [
          { label: "I have completed my workup and am choosing a donor", value: "donor_egg_selecting_donor" },
          { label: "My clinic is creating embryos using donor eggs", value: "donor_egg_embryo_creation" },
          {
            label: "The embryos are ready and I'm preparing for my transfer",
            value: "donor_egg_transfer_prep",
          },
          {
            label: "I've had my transfer and I'm waiting on results",
            value: "donor_egg_waiting_pregnancy_results",
          },
        ];
      case "fertility_preservation":
        return [
          {
            label: "I have a protocol and I'm preparing for my cycle — but haven't started medications yet",
            value: "fertility_preservation_ivf_prep",
          },
          { label: "I've started medications for this cycle", value: "fertility_preservation_started_stims" },
          {
            label: "I've completed my retrieval",
            value: "fertility_preservation_retrieval_completed",
          },
        ];
      default:
        return [{ label: "I don't know yet", value: "unknown" }];
    }
  };

  const options = getOptionsForJourney(selectedJourney);

  const handleSubmit = (values: Values, formikHelpers: FormikHelpers<Values>) => {
    onSubmit(values, formikHelpers);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
  });

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            description="This helps us tailor your care to where you are in your journey right now."
            question="Where are you right now in your treatment at your current clinic?"
          />
          <MilestoneSelectInput
            autoAdvance
            defaultValue={formik.values.upcomingMilestone}
            name="upcomingMilestone"
            options={options}
          />
        </QuestionContainer>
        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
